import { useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams, type To } from "react-router";
import {
  averageBy,
  averageOf,
  countBy,
  crossTab,
  groupedCrossTab,
  headline,
  monthlyAverage,
  monthlyStack,
  percent,
  summariseIncidents,
  summariseTriage,
  topWithOthers,
  type FilterKey,
  type ReportTicket,
} from "@server/reporting/metrics";
import type { IncidentRow } from "@server/reporting/types";
import { BarChart, Gauge, LineChart, PieChart, StackedBars } from "./charts";
import { drillSearch } from "./filters";
import { CrossTabTable, IncidentTable, TicketTable } from "./tables";

// Pick-up and waiting dates only started to be recorded in March 2026, so the lifecycle gauges look at tickets raised since then.
export const LIFECYCLE_SINCE = "2026-03-17";
// Days the team aims to close a ticket in.
const TARGET_DAYS = 5;

const count = new Intl.NumberFormat("en-GB");

function Card({ title, note, children, wide }: { title: string; note?: string; children: ReactNode; wide?: boolean }) {
  return (
    <section className={`report-card${wide ? " wide" : ""}`}>
      <h3>{title}</h3>
      {note && <p className="record-note">{note}</p>}
      {children}
    </section>
  );
}

function Stat({ label, value, sub, to }: { label: string; value: string; sub?: string; to?: To }) {
  const body = (
    <>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      {sub && <span className="stat-sub">{sub}</span>}
      {to && <span className="stat-more">See the tickets →</span>}
    </>
  );
  return to ? (
    <Link to={to} className="stat stat-link">
      {body}
    </Link>
  ) : (
    <div className="stat">{body}</div>
  );
}

// Opens the tickets behind a figure on the All tickets tab, keeping the filters already applied.
function useDrill() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const to = (change: Parameters<typeof drillSearch>[1]): To => ({ pathname: "/reports/tickets", search: drillSearch(params, change) });
  return { to, byValue: (key: FilterKey) => (label: string) => navigate(to({ key, value: label })) };
}

interface TabProps {
  // The tickets that pass the filters, and every ticket.
  rows: ReportTicket[];
  all: ReportTicket[];
}

export function HeadlineTab({ rows, all }: TabProps) {
  const drill = useDrill();
  const now = headline(rows);
  const total = headline(all);
  const filtered = rows.length !== all.length;
  return (
    <div className="report-grid">
      <Stat label="Tickets raised" value={count.format(now.raised)} sub={filtered ? `${count.format(total.raised)} all time` : undefined} />
      <Stat label="Tickets closed" value={count.format(now.closed)} sub={filtered ? `${count.format(total.closed)} all time` : undefined} />
      <Card title="Average business days to close a ticket" note={filtered ? `All time: ${total.averageDaysToClose?.toFixed(2) ?? "–"}` : undefined}>
        <Gauge value={now.averageDaysToClose} target={TARGET_DAYS} caption="Business days from raised to closed" />
      </Card>
      <Card title="ENG ticket type">
        <PieChart data={countBy(rows, "ticketType")} title="Ticket type" onSelect={drill.byValue("ticketType")} />
      </Card>
      <Card title="Discussed in the CS huddle first?" note="Only tickets where the question was answered. It was added in February 2026.">
        <PieChart data={countBy(rows, "huddle", { skipNone: true })} title="Discussed in the CS huddle" onSelect={drill.byValue("huddle")} />
      </Card>
      <Card title="Tickets raised by business unit">
        <PieChart data={countBy(rows, "businessUnit")} title="Business unit" onSelect={drill.byValue("businessUnit")} />
      </Card>
      <Card title="Ticket severity">
        <PieChart data={countBy(rows, "severity")} title="Severity" onSelect={drill.byValue("severity")} />
      </Card>
    </div>
  );
}

export function TimeTab({ rows }: TabProps) {
  const now = headline(rows);
  const since = rows.filter((row) => row.raisedDay >= LIFECYCLE_SINCE);
  const lifecycle = [
    { key: "pickedUpToClose", caption: "Picked up by engineering to closed" },
    { key: "waitingCsToClose", caption: "Waiting on CS to closed" },
    { key: "waitingCustomerToClose", caption: "Waiting on customer to closed" },
  ] as const;
  return (
    <div className="report-grid">
      <Card title="Average business days to close a ticket">
        <Gauge value={now.averageDaysToClose} target={TARGET_DAYS} caption="Business days from raised to closed" />
      </Card>
      <Card title="Average business days to close, by month raised" wide>
        <LineChart points={monthlyAverage(rows, "daysToClose").map((month) => ({ label: month.label, value: month.average }))} title="Average business days to close by month raised" />
      </Card>
      {lifecycle.map(({ key, caption }) => {
        const result = averageOf(since, key);
        return (
          <Card key={key} title={caption} note={`Tickets raised since 17 March 2026, when these dates started to be recorded. ${count.format(result.count)} tickets.`}>
            <Gauge value={result.average} target={TARGET_DAYS} caption="Business days" />
          </Card>
        );
      })}
    </div>
  );
}

const SEVERITY_ORDER = ["Low", "Medium", "High", "Urgent"];

export function SeverityTab({ rows }: TabProps) {
  const drill = useDrill();
  const averages = averageBy(rows, "severity", "daysToClose");
  const ordered = [...SEVERITY_ORDER, ...averages.map((item) => item.label).filter((label) => !SEVERITY_ORDER.includes(label))];
  return (
    <div className="report-grid">
      <Card title="Ticket severity">
        <PieChart data={countBy(rows, "severity")} title="Severity" onSelect={drill.byValue("severity")} />
      </Card>
      <Card title="Average business days to close, by severity" wide>
        <BarChart
          bars={ordered.flatMap((label) => {
            const item = averages.find((candidate) => candidate.label === label);
            return item ? [{ label, value: item.average }] : [];
          })}
          target={TARGET_DAYS}
          targetLabel={`Target: ${TARGET_DAYS} days`}
          title="Average business days to close by severity"
        />
      </Card>
      <Card title="Severity by business unit" wide>
        <CrossTabTable table={crossTab(rows, "severity", "businessUnit")} rowHeader="Severity" columnHeader="Business unit" />
      </Card>
    </div>
  );
}

export function RaisedTab({ rows }: TabProps) {
  const drill = useDrill();
  return (
    <div className="report-grid">
      <Stat label="Tickets raised" value={count.format(rows.length)} />
      <Card title="Tickets raised each month, by ENG team" wide>
        <StackedBars stack={monthlyStack(rows, "team")} title="Tickets raised each month by team" />
      </Card>
      <Card title="Tickets raised each month, by business unit" wide>
        <StackedBars stack={monthlyStack(rows, "businessUnit")} title="Tickets raised each month by business unit" />
      </Card>
      <Card title="Tickets raised by business unit">
        <PieChart data={countBy(rows, "businessUnit")} title="Business unit" onSelect={drill.byValue("businessUnit")} />
      </Card>
    </div>
  );
}

const TOP_FEATURES = 10;

export function FeaturesTab({ rows }: TabProps) {
  const drill = useDrill();
  const [showAll, setShowAll] = useState(false);
  const maxRows = showAll ? undefined : TOP_FEATURES;
  return (
    <div className="report-grid">
      <Card title="Tickets by feature" note={`The ${TOP_FEATURES} most used features, and the rest together.`}>
        <PieChart data={topWithOthers(countBy(rows, "feature"), TOP_FEATURES)} title="Tickets by feature" onSelect={drill.byValue("feature")} />
      </Card>
      <div className="report-options">
        <label>
          <input type="checkbox" checked={showAll} onChange={(event) => setShowAll(event.target.checked)} /> Show every feature in the tables (not just the top {TOP_FEATURES})
        </label>
      </div>
      <Card title="Features worked on, by business unit" wide>
        <CrossTabTable table={crossTab(rows, "feature", "businessUnit", { maxRows })} rowHeader="Feature" columnHeader="Business unit" />
      </Card>
      <Card title="Features worked on, by ENG team" wide>
        <CrossTabTable table={crossTab(rows, "feature", "team", { maxRows })} rowHeader="Feature" columnHeader="Team who worked on it" />
      </Card>
      <Card title="Features worked on, by pricing plan" wide>
        <CrossTabTable table={crossTab(rows, "feature", "pricingPlan", { maxRows })} rowHeader="Feature" columnHeader="Pricing plan" />
      </Card>
    </div>
  );
}

const TOP_RAISERS = 10;
const RAISER_FEATURES = 7;

export function ClosedTab({ rows }: TabProps) {
  const closed = rows.filter((row) => row.closed);
  return (
    <div className="report-grid">
      <Stat label="Tickets closed" value={count.format(closed.length)} />
      <Card title="Closed tickets, by feature and status" wide>
        <CrossTabTable table={crossTab(closed, "feature", "status", { maxRows: TOP_FEATURES })} rowHeader="Feature" columnHeader="Status" />
      </Card>
      <Card title="Closed tickets, by who raised them" note={`The ${TOP_RAISERS} people who raised the most, and each one's ${RAISER_FEATURES} most used features.`} wide>
        {groupedCrossTab(closed, "raiser", "feature", "status", { maxGroups: TOP_RAISERS, maxRowsPerGroup: RAISER_FEATURES }).map((group) => (
          <div key={group.label} className="raiser-group">
            <h4>{group.label}</h4>
            <CrossTabTable table={group.table} rowHeader="Feature" columnHeader="Status" />
          </div>
        ))}
      </Card>
      <Card title="Closed tickets" wide>
        <TicketTable rows={closed} />
      </Card>
    </div>
  );
}

export function TriageTab({ rows, noTicket }: TabProps & { noTicket: ReportTicket[] }) {
  const drill = useDrill();
  const summary = summariseTriage(rows, noTicket);
  const decided = summary.overrides + summary.acceptedNoTicket;
  return (
    <div className="report-grid">
      <Stat label="Tickets AI triage assessed" value={count.format(summary.triaged)} sub="Raised in Triage Desk" />
      <Stat
        label='Raised despite a "no ticket" verdict'
        value={count.format(summary.overrides)}
        sub={`${percent(summary.overrides, decided)} of ${count.format(decided)} "no ticket" verdicts the person acted on`}
        to={summary.overrides > 0 ? drill.to({ triage: "overridden" }) : undefined}
      />
      <Stat
        label='Closed after a "no ticket" verdict'
        value={count.format(summary.acceptedNoTicket)}
        sub={`${count.format(summary.expiredNoTicket)} more closed automatically after a day with no action`}
      />
      <Stat
        label="Type changed by the person"
        value={count.format(summary.typeChanges)}
        sub={`${percent(summary.typeChanges, summary.classified)} of tickets triage classified`}
        to={summary.typeChanges > 0 ? drill.to({ triage: "typeChanged" }) : undefined}
      />
      <Card
        title="How the type was changed"
        note="Triage classifies a ticket as a Bug or a Customer Fix. This counts the tickets where the person changed that before raising it."
      >
        <PieChart data={summary.typeChangesBy} title="Type changes" />
      </Card>
      <Card title='What people did after a "no ticket" verdict, by month' wide>
        <StackedBars
          stack={{
            months: summary.byMonth.map(({ key, label }) => ({ key, label })),
            labels: ["Raised the ticket anyway", "Closed without a ticket"],
            counts: summary.byMonth.map((month) => [month.overrides, month.acceptedNoTicket]),
          }}
          title="Overrides and closed submissions by month"
        />
      </Card>
      <Card title="Type changed by the person, by month" wide>
        <BarChart bars={summary.byMonth.map((month) => ({ label: month.label, value: month.typeChanges }))} title="Type changes by month" />
      </Card>
      <Card title="What these numbers cover" wide>
        <p className="record-note">
          Only submissions raised in Triage Desk that triage assessed; tickets imported from the Sheet, and submissions raised while triage was unavailable, are left out. A "no
          ticket" verdict nobody has acted on yet is not counted until the person acts or it is closed automatically. Submissions closed before this was recorded are not included.
        </p>
      </Card>
    </div>
  );
}

export function TicketsTab({ rows }: TabProps) {
  return (
    <div className="report-grid">
      <Card title="Every ticket" wide>
        <TicketTable rows={rows} />
      </Card>
    </div>
  );
}

export function IncidentsTab({ incidents }: { incidents: IncidentRow[] }) {
  const summary = summariseIncidents(incidents);
  return (
    <div className="report-grid">
      <Stat label="Incidents raised" value={count.format(summary.total)} />
      <Card title="Incidents by severity">
        <PieChart data={summary.bySeverity} title="Incidents by severity" unit="incident" />
      </Card>
      <Card title="Incidents by type" note="An incident can have more than one type.">
        <PieChart data={summary.byType} title="Incidents by type" unit="incident" />
      </Card>
      <Card title="Incidents raised each month" wide>
        <BarChart bars={summary.byMonth.map((month) => ({ label: month.label, value: month.count }))} title="Incidents raised each month" />
      </Card>
      <Card title="All incidents" wide>
        <IncidentTable rows={incidents} />
      </Card>
    </div>
  );
}
